import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, XCircle, ShieldCheck, ShieldAlert, Loader2, RefreshCw, FileText, Info } from 'lucide-react';
import {
  ClientOnboardingDraft,
  PrimaryUserRole,
  MAX_DISPLAY_NAME_LEN,
  MAX_LEGAL_NAME_LEN,
  MAX_PHONE_LEN,
  MAX_ADDRESS_LEN,
  MAX_FARM_NAME_LEN,
  MAX_FARM_ADDRESS_LEN,
  MAX_PRIMARY_FIRST_NAME_LEN,
  MAX_PRIMARY_LAST_NAME_LEN,
  MAX_MAPPING_FILE_NAME_LEN,
  MAX_INFRA_ID_LEN,
  MAX_INFRA_LABEL_LEN,
  MAX_FARMS_PER_DRAFT,
  MAX_INFRASTRUCTURE_ITEMS_PER_DRAFT,
  MAX_CLIENT_EMAIL_LEN,
  MAX_PRIMARY_EMAIL_FILTER_SAFE_LEN,
} from '../../../../types/clientOnboarding';
import { generatePlan, PlanData, ClientOnboardingApiError } from '../../../../lib/clientOnboardingApi';
import ClientCreation from '../ClientCreation';
import { supportsClientCreation } from '../../../../config/clientOnboardingExecution';

interface Props {
  draft: ClientOnboardingDraft;
  errorKeys: string[];
}

type PlanStatus = 'IDLE' | 'PLANNING' | 'READY' | 'BLOCKED' | 'ERROR';

const badgeCls = (status: 'complete' | 'incomplete' | 'disabled' | 'warning'): string => {
  switch (status) {
    case 'complete': return 'bg-green-100 text-green-700';
    case 'incomplete': return 'bg-amber-100 text-amber-700';
    case 'disabled': return 'bg-red-100 text-red-700';
    case 'warning': return 'bg-gray-100 text-gray-600';
    default: return 'bg-gray-100 text-gray-600';
  }
};

const iconFor = (status: 'complete' | 'incomplete' | 'disabled' | 'warning') => {
  switch (status) {
    case 'complete': return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    case 'incomplete': return <AlertTriangle className="w-4 h-4 text-amber-600" />;
    case 'disabled': return <XCircle className="w-4 h-4 text-red-600" />;
    case 'warning': return <AlertTriangle className="w-4 h-4 text-gray-400" />;
  }
};

// ---- R1E Sweep A: Format raw error keys into human-readable messages ----
function formatValidationError(
  key: string,
  draft: ClientOnboardingDraft,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  // Parse dynamic indices from the key
  const farmMatch = key.match(/^farms\.(\d+)\.(.+)$/);
  const infraMatch = key.match(/^infra\.(.+?)\.(.+)$/);

  if (farmMatch) {
    const farmIdx = parseInt(farmMatch[1], 10) + 1; // human-readable 1-based
    const field = farmMatch[2];
    const farmName = draft.farms[parseInt(farmMatch[1], 10)]?.farm_name || `#${farmIdx}`;
    const fieldLabels: Record<string, string> = {
      farm_name: t('step2.farm_name'),
      surface_ha: t('step2.surface_ha'),
      latitude: t('step2.latitude'),
      longitude: t('step2.longitude'),
      address: t('step2.address'),
      country: t('step2.country'),
    };
    const label = fieldLabels[field] || field;

    // R2J-04: Localized per-farm max/aggregate/country validations
    if (field === 'farm_name_max') return `${farmName} — ${t('step2.farm_name')} — ${t('validation.fieldTooLong', { max: MAX_FARM_NAME_LEN })}`;
    if (field === 'address_max') return `${farmName} — ${t('step2.address')} — ${t('validation.fieldTooLong', { max: MAX_FARM_ADDRESS_LEN })}`;
    if (field === 'country_invalid') return `${farmName} — ${t('step2.country')} — ${t('validation.invalidCountry')}`;
    if (field === 'aggregate_too_large') return `${farmName} — ${t('validation.aggregateTooLarge')}`;

    return `${farmName} — ${label} — ${t('validation.invalidValue')}`;
  }

  if (infraMatch) {
    const itemId = infraMatch[1];
    const field = infraMatch[2];
    const item = draft.infrastructure.find(i => i.id === itemId);
    const itemName = item?.label || item?.category || itemId;

    // R2J-04: Localized infra max validations
    if (field === 'id_max') return `${itemName} — ${t('validation.fieldTooLong', { max: MAX_INFRA_ID_LEN })}`;
    if (field === 'label_max') return `${itemName} — ${t('validation.fieldTooLong', { max: MAX_INFRA_LABEL_LEN })}`;

    return `${itemName} — ${t('validation.invalidValue')}`;
  }

  // Static key mappings
  const staticMappings: Record<string, string> = {
    'client.display_name': `${t('step1.display_name')} — ${t('validation.required')}`,
    'client.display_name_max': `${t('step1.display_name')} — ${t('validation.fieldTooLong', { max: MAX_DISPLAY_NAME_LEN })}`,
    'client.legal_name': `${t('step1.legal_name')} — ${t('validation.required')}`,
    'client.legal_name_max': `${t('step1.legal_name')} — ${t('validation.fieldTooLong', { max: MAX_LEGAL_NAME_LEN })}`,
    'client.country': `${t('step1.country')} — ${t('validation.required')}`,
    'client.country_invalid': `${t('step1.country')} — ${t('validation.invalidCountry')}`,
    'client.email': `${t('step1.email')} — ${t('validation.invalidEmail')}`,
    'client.email_max': `${t('step1.email')} — ${t('validation.fieldTooLong', { max: MAX_CLIENT_EMAIL_LEN })}`,
    'client.phone_max': `${t('step1.phone')} — ${t('validation.fieldTooLong', { max: MAX_PHONE_LEN })}`,
    'client.address_max': `${t('step1.address')} — ${t('validation.fieldTooLong', { max: MAX_ADDRESS_LEN })}`,
    'client.language': `${t('step1.language')} — ${t('validation.invalidLanguage')}`,
    'client.currency': `${t('step1.currency')} — ${t('validation.invalidCurrency')}`,
    'client.timezone': `${t('step1.timezone')} — ${t('validation.invalidTimezone')}`,
    'client.aggregate_too_large': t('validation.aggregateTooLarge'),
    'farms.empty': t('validation.farmRequired'),
    'farms.too_many': t('validation.tooManyItems', { max: MAX_FARMS_PER_DRAFT }),
    'infrastructure.too_many': t('validation.tooManyItems', { max: MAX_INFRASTRUCTURE_ITEMS_PER_DRAFT }),
    'primary_user.first_name': `${t('step3.first_name')} — ${t('validation.required')}`,
    'primary_user.first_name_max': `${t('step3.first_name')} — ${t('validation.fieldTooLong', { max: MAX_PRIMARY_FIRST_NAME_LEN })}`,
    'primary_user.last_name': `${t('step3.last_name')} — ${t('validation.required')}`,
    'primary_user.last_name_max': `${t('step3.last_name')} — ${t('validation.fieldTooLong', { max: MAX_PRIMARY_LAST_NAME_LEN })}`,
    'primary_user.email': `${t('step3.email')} — ${t('validation.invalidEmail')}`,
    'primary_user.email_max': `${t('step3.email')} — ${t('validation.fieldTooLong', { max: MAX_PRIMARY_EMAIL_FILTER_SAFE_LEN })}`,
    'commercial.command_packs': `${t('step4.commandPacks')} — ${t('validation.nonNegativeInteger')}`,
    'commercial.vision_packs': `${t('step4.visionPacks')} — ${t('validation.nonNegativeInteger')}`,
    'commercial.ia_packs': `${t('step4.iaPacks')} — ${t('validation.nonNegativeInteger')}`,
    'commercial.planned_gateways': `${t('step4.plannedGateways')} — ${t('validation.nonNegativeInteger')}`,
    'commercial.pump_power': `${t('step4.pumpRatedPower')} — ${t('validation.positiveNumber')}`,
    'mapping.manual_count': `${t('step5.drawManually')} — ${t('validation.nonNegativeInteger')}`,
    'mapping.file_name': `${t('step5.importKML')} — ${t('validation.required')}`,
    'mapping.file_name_max': `${t('step5.importKML')} — ${t('validation.fieldTooLong', { max: MAX_MAPPING_FILE_NAME_LEN })}`,
    'mapping.file_size_bytes': `${t('step5.importKML')} — ${t('validation.invalidValue')}`,
    'mapping.file_size': `${t('step5.importKML')} — ${t('validation.mappingFileTooLarge')}`,
    'safety.commands': t('validation.safetyViolation'),
    'safety.downlinks': t('validation.safetyViolation'),
    'safety.commissioned': t('validation.safetyViolation'),
  };

  if (staticMappings[key]) return staticMappings[key];

  // Fallback — still use the key but add prefix to distinguish from raw
  return `${t('validation.validationIssue')}: ${key}`;
}

const ReviewStep: React.FC<Props> = ({ draft, errorKeys }) => {
  const { t } = useTranslation(['admin_client_onboarding']);

  // ---- Server plan state management ----
  const [planStatus, setPlanStatus] = useState<PlanStatus>('IDLE');
  const [planResult, setPlanResult] = useState<PlanData | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);

  // ---- Invalidate plan state when draft changes ----
  const prevDraftRef = useRef(draft);
  useEffect(() => {
    if (prevDraftRef.current !== draft) {
      prevDraftRef.current = draft;
      setPlanStatus('IDLE');
      setPlanResult(null);
      setPlanError(null);
    }
  }, [draft]);

  // ---- R1D-03: Localize role in review summary ----
  const roleLabels: Record<PrimaryUserRole, string> = {
    owner: t('step3.roles.owner'),
    farm_manager: t('step3.roles.farm_manager'),
    technician: t('step3.roles.technician'),
    viewer: t('step3.roles.viewer'),
  };

  const localizedRole = draft.primary_user.role && roleLabels[draft.primary_user.role]
    ? roleLabels[draft.primary_user.role]
    : draft.primary_user.role || '—';

  // ---- R1A-02B: Real validation — consume validateDraft result ----
  const valid = errorKeys.length === 0;

  const DRY_RUN_FAIL_ID = 'onboarding-dry-run-fail';

  // ---- R1C-07: Section status derived from errorKeys AND data ----
  const hasAnyError = (prefix: string) => errorKeys.some(k => k.startsWith(prefix));

  const clientHasErrors = hasAnyError('client.');
  const farmHasErrors = hasAnyError('farms.');
  const userHasErrors = hasAnyError('primary_user.');
  const offerHasErrors = hasAnyError('commercial.');
  const mappingHasErrors = hasAnyError('mapping.');
  const infraHasErrors = hasAnyError('infra.');

  const clientHasData = !!(draft.client.display_name && draft.client.legal_name && draft.client.country && draft.client.email);
  const farmHasData = draft.farms.length > 0 && draft.farms.every(f => f.farm_name.trim().length >= 2);
  const userHasData = !!(draft.primary_user.first_name && draft.primary_user.last_name && draft.primary_user.email);
  const offerHasData = true;
  const mappingHasData = !!(draft.mapping.file_name || draft.mapping.manual_count > 0);
  const infraHasData = draft.infrastructure.length > 0;

  const statusFor = (hasErrors: boolean, hasData: boolean, forceWarning: boolean): 'complete' | 'incomplete' | 'warning' => {
    if (hasErrors) return 'incomplete';
    if (!hasData && forceWarning) return 'warning';
    if (!hasData) return 'incomplete';
    return 'complete';
  };

  const sectionLabels: Record<string, string> = {
    client: t('step8.sections.client'),
    farm: t('step8.sections.farm'),
    user: t('step8.sections.user'),
    offer: t('step8.sections.offer'),
    mapping: t('step8.sections.mapping'),
    infrastructure: t('step8.sections.infrastructure'),
    ttn: t('step8.sections.ttn'),
    safety: t('step8.sections.safety'),
  };

  const sections = [
    {
      key: 'client',
      status: statusFor(clientHasErrors, clientHasData, false),
      summary: draft.client.display_name
        ? `${draft.client.display_name} (${draft.client.country || '—'})`
        : t('step8.noData'),
    },
    {
      key: 'farm',
      status: statusFor(farmHasErrors, farmHasData, false),
      summary: draft.farms.length > 0
        ? draft.farms.map(f => f.farm_name || '—').join(', ')
        : t('step8.noData'),
    },
    {
      key: 'user',
      status: statusFor(userHasErrors, userHasData, false),
      summary: draft.primary_user.first_name
        ? `${draft.primary_user.first_name} ${draft.primary_user.last_name} (${localizedRole})`
        : t('step8.noData'),
    },
    {
      key: 'offer',
      status: offerHasErrors ? 'incomplete' as const : 'complete' as const,
      summary: `${draft.commercial_scope.command_packs ?? 0}C / ${draft.commercial_scope.vision_packs ?? 0}V / ${draft.commercial_scope.ia_packs ?? 0}IA`,
    },
    {
      key: 'mapping',
      status: statusFor(mappingHasErrors, mappingHasData, true),
      summary: draft.mapping.file_name
        ? `${draft.mapping.file_name}`
        : draft.mapping.manual_count > 0
          ? `${draft.mapping.manual_count} ${t('step8.optional')}`
          : t('step8.optional'),
    },
    {
      key: 'infrastructure',
      status: statusFor(infraHasErrors, infraHasData, true),
      summary: draft.infrastructure.length > 0
        ? `${draft.infrastructure.length} ${sectionLabels.infrastructure.toLowerCase()}`
        : t('step8.awaitingHardware'),
    },
    {
      key: 'ttn', status: 'disabled' as const,
      summary: t('step8.notConfigured'),
    },
    {
      key: 'safety', status: 'disabled' as const,
      summary: `${t('step8.commandsDisabled')}, ${t('step8.downlinksDisabled')}, ${t('step8.commissioningDisabled')}`,
    },
  ];

  // ---- Semantic blocker code → i18n key mapping (P2-07) ----
  const blockerLabel = (blocker: { code: string; message_key?: string | null }): string => {
    // Explicit semantic mapping — NEVER trust raw message_key from server
    const mapping: Record<string, string> = {
      ORGANIZATION_ALREADY_EXISTS: t('step8.blockers.organizationAlreadyExists'),
      COGNITO_IDENTITY_DISABLED: t('step8.blockers.cognitoIdentityDisabled'),
      COGNITO_IDENTITY_AMBIGUOUS: t('step8.blockers.cognitoIdentityAmbiguous'),
      DUPLICATE_FARM_NAME: t('step8.blockers.duplicateFarmName'),
      DUPLICATE_INFRASTRUCTURE_ID: t('step8.blockers.duplicateInfrastructureId'),
      DEPENDENCY_UNAVAILABLE: t('step8.blockers.dependencyUnavailable'),
    };
    if (mapping[blocker.code]) return mapping[blocker.code];
    // Fallback: localized generic blocker + safe machine code
    return `${t('step8.blockers.genericBlocker')} (${blocker.code})`;
  };

  // ---- Semantic warning code → i18n key mapping (P2-06) ----
  const warningLabel = (w: { code: string; message_key?: string | null }): string => {
    const mapping: Record<string, string> = {
      MAPPING_METADATA_ONLY: t('step8.warnings.mappingMetadataOnly'),
      GLOBAL_NAME_UNIQUENESS_NOT_CHECKED: t('step8.warnings.globalNameUniquenessNotChecked'),
    };
    if (mapping[w.code]) return mapping[w.code];
    return `${t('step8.warnings.unknown')} (${w.code})`;
  };

  // ---- API error → localized message (ARCH P2-04) ----
  const apiErrorMessage = (err: unknown): string => {
    if (err instanceof ClientOnboardingApiError) {
      switch (err.code) {
        case 'network_error': return t('step8.errors.networkError');
        case 'unauthenticated': return t('step8.errors.unauthenticated');
        case 'forbidden': return t('step8.errors.forbidden');
        case 'validation_error': return t('step8.errors.validationError');
        case 'dependency_unavailable': return t('step8.errors.dependencyUnavailable');
        case 'server_error': return t('step8.errors.serverError');
        default: return t('step8.errors.unknownError');
      }
    }
    // Fallback — never expose raw exception text
    return t('step8.errors.unknownError');
  };

  // ---- Generate plan handler with double-click prevention ----
  const handleGeneratePlan = async () => {
    if (!valid || planStatus === 'PLANNING') return;
    setPlanStatus('PLANNING');
    setPlanError(null);
    try {
      const result = await generatePlan(draft);
      setPlanResult(result);
      setPlanStatus(result.status);
    } catch (err: unknown) {
      setPlanError(apiErrorMessage(err));
      setPlanStatus('ERROR');
    }
  };

  return (
    <div className="space-y-4" data-testid="onboarding-step-review">
      <div>
        <h3 className="text-heading-3 text-[var(--text-primary)] mb-1">{t('step8.heading')}</h3>
        <p className="text-sm text-[var(--text-secondary)]">{t('step8.description')}</p>
      </div>

      {/* R1E Sweep A: Human-readable validation errors — no raw keys exposed */}
      {!valid && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="onboarding-validation-errors">
          <ShieldAlert className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">{t('step8.invalidDryRunTitle')}</p>
            <p className="text-sm text-amber-700">{t('step8.validationErrorCount', { count: errorKeys.length })}</p>
            <ul className="text-xs text-amber-700 mt-1 list-disc list-inside">
              {errorKeys.slice(0, 8).map(key => (
                <li key={key}>{formatValidationError(key, draft, t)}</li>
              ))}
              {errorKeys.length > 8 && <li>...</li>}
            </ul>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {sections.map(section => (
          <div key={section.key} className="flex items-center justify-between p-3 border border-[var(--border-light)] rounded-lg" data-testid={`review-${section.key}`}>
            <div className="flex items-center gap-3">
              {iconFor(section.status)}
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">{sectionLabels[section.key]}</p>
                <p className="text-xs text-[var(--text-secondary)]">{section.summary}</p>
              </div>
            </div>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeCls(section.status)}`}>
              {section.status === 'complete' ? t('step8.complete')
                : section.status === 'disabled' ? t('step8.disabled')
                : section.status === 'warning' ? t('step8.optional')
                : t('step8.incomplete')}
            </span>
          </div>
        ))}
      </div>

      {/* ---- Server plan status panels (single local validation panel, no duplicate) ---- */}

      {/* IDLE: local validation passed, ready to generate server plan */}
      {valid && planStatus === 'IDLE' && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-[var(--color-success)]/10 border border-[var(--color-success)]/30" data-testid="onboarding-dry-run-success">
          <ShieldCheck className="w-6 h-6 text-[var(--color-success)] shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-[var(--color-success)]">{t('step8.dryRunTitle')}</p>
            <p className="text-sm text-[var(--text-secondary)]">{t('step8.localValidationReady')}</p>
          </div>
        </div>
      )}

      {/* PLANNING: server plan generation in progress */}
      {planStatus === 'PLANNING' && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200" data-testid="onboarding-planning">
          <Loader2 className="w-6 h-6 text-blue-600 shrink-0 mt-0.5 animate-spin" />
          <div>
            <p className="font-semibold text-blue-800">{t('step8.planning')}</p>
            <p className="text-sm text-blue-700">{t('step8.zeroWriteConfirmation')}</p>
          </div>
        </div>
      )}

      {/* READY: server plan generated successfully */}
      {planStatus === 'READY' && planResult && (
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-4 rounded-lg bg-[var(--color-success)]/10 border border-[var(--color-success)]/30" data-testid="onboarding-plan-ready">
            <ShieldCheck className="w-6 h-6 text-[var(--color-success)] shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-[var(--color-success)]">{t('step8.serverPlanReadyTitle')}</p>
              <p className="text-sm text-[var(--text-secondary)]">{t('step8.serverPlanReady')}</p>
            </div>
          </div>
          {/* P2-06: Render server warnings for READY plans */}
          {planResult.warnings && planResult.warnings.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 space-y-1" data-testid="onboarding-plan-warnings">
              <p className="text-xs font-semibold text-amber-700 uppercase">{t('step8.warnings.heading')}</p>
              {planResult.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-amber-700">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{warningLabel(w)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="p-4 rounded-lg bg-white border border-[var(--border-light)] space-y-2" data-testid="onboarding-plan-summary">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-[var(--text-secondary)]" />
              <span className="text-[var(--text-secondary)]">{t('step8.planId')}:</span>
              <span className="font-mono font-medium text-[var(--text-primary)]">{planResult.plan_id}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[var(--text-secondary)]">{t('step8.operationCount')}:</span>
              <span className="font-medium text-[var(--text-primary)]">{planResult.operations.length}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[var(--color-success)]">
              <CheckCircle2 className="w-4 h-4" />
              <span>{t('step8.zeroWriteConfirmation')}</span>
            </div>
          </div>
        </div>
      )}

      {/* BLOCKED: server plan cannot proceed */}
      {planStatus === 'BLOCKED' && planResult && (
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200" data-testid="onboarding-plan-blocked">
            <ShieldAlert className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800">{t('step8.serverPlanBlockedTitle')}</p>
              <p className="text-sm text-red-700">{t('step8.serverPlanBlocked')}</p>
            </div>
          </div>
          {/* P2-06: Show warnings even when BLOCKED */}
          {planResult.warnings && planResult.warnings.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 space-y-1" data-testid="onboarding-plan-warnings">
              <p className="text-xs font-semibold text-amber-700 uppercase">{t('step8.warnings.heading')}</p>
              {planResult.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-amber-700">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{warningLabel(w)}</span>
                </div>
              ))}
            </div>
          )}
          {/* P2-07: Translate blockers — NEVER render raw message_key */}
          {planResult.collisions.filter(c => c.severity === 'BLOCKER').length > 0 && (
            <ul className="text-sm text-red-700 list-disc list-inside space-y-1 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="onboarding-plan-blockers">
              {planResult.collisions.filter(c => c.severity === 'BLOCKER').map((b, i) => (
                <li key={i}>{blockerLabel(b)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ERROR: server plan generation failed */}
      {planStatus === 'ERROR' && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200" data-testid="onboarding-plan-error">
          <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800">{t('step8.serverPlanError')}</p>
            {planError && <p className="text-sm text-red-700 mt-1">{planError}</p>}
          </div>
        </div>
      )}

      {/* Dry-run fail panel — shown only when draft is invalid */}
      {!valid && (
        <div id={DRY_RUN_FAIL_ID} className="p-4 rounded-lg bg-red-50 border border-red-200 text-center" data-testid="onboarding-dry-run-fail">
          <p className="text-sm text-red-700 font-medium">{t('step8.invalidDryRunHelp')}</p>
        </div>
      )}

      {/* Action buttons */}
      {supportsClientCreation() && planResult?.status === 'READY' &&
        <ClientCreation draft={draft} plan={planResult} />}
      <div className="flex items-center gap-3 pt-4">
        <button disabled
          className="flex-1 px-4 py-2.5 bg-[var(--gray-100)] text-[var(--text-tertiary)] rounded-lg cursor-not-allowed text-sm"
          data-testid="onboarding-save-draft-btn"
          title={t('step8.saveDraftDisabled')}>
          {t('step8.saveDraft')}
        </button>
        {planStatus === 'ERROR' ? (
          <button
            className="flex-1 px-4 py-2.5 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 text-sm font-medium flex items-center justify-center gap-2"
            data-testid="onboarding-retry-btn"
            onClick={handleGeneratePlan}>
            <RefreshCw className="w-4 h-4" />
            {t('step8.serverPlanRetry')}
          </button>
        ) : (
          <button
            className="flex-1 px-4 py-2.5 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="onboarding-generate-plan-btn"
            disabled={!valid || planStatus === 'PLANNING' || planStatus === 'READY'}
            aria-describedby={!valid ? DRY_RUN_FAIL_ID : undefined}
            onClick={handleGeneratePlan}>
            {planStatus === 'PLANNING' ? t('step8.planning') : t('step8.generateServerPlan')}
          </button>
        )}
      </div>
    </div>
  );
};

export default ReviewStep;
